import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  HypothesesBaselineState as BaselineState,
  HypothesesHypothesis as Hypothesis,
  HypothesesHypothesisConfig as HypothesisConfig,
  HypothesesHypothesisCreate as HypothesisCreate,
  HypothesesHypothesisImport as HypothesisImport,
  HypothesesHypothesisImportHistory as HypothesisImportHistory,
  HypothesesHypothesisOutcome as HypothesisOutcome,
  HypothesesReviewBaselineCheck as ReviewBaselineCheck,
  HypothesesReviewCanFail as ReviewCanFail,
  HypothesesReviewSourceCheck as ReviewSourceCheck,
  HypothesesHypothesisStatus as HypothesisStatus,
  HypothesesHypothesisStatusKey as HypothesisStatusKey,
  HypothesesHypothesisSummary as HypothesisSummary,
  HypothesesHypothesisUpdate as HypothesisUpdate,
  HypothesesSourceClaim as SourceClaim,
  HypothesesThresholdDirection as ThresholdDirection,
  HypothesesUpdateFrequency as UpdateFrequency,
} from '../generated/api/types.gen.ts';
import { hypothesesConfig, hypothesesCreate, hypothesesGet, hypothesesImportHypothesis, hypothesesList, hypothesesSubmit, hypothesesUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { HypothesisImport, HypothesisImportHistory, HypothesisOutcome, ReviewBaselineCheck, ReviewCanFail, ReviewSourceCheck };
export type { BaselineState, Hypothesis, HypothesisConfig, HypothesisCreate, HypothesisStatus, HypothesisStatusKey, HypothesisSummary, HypothesisUpdate, SourceClaim, ThresholdDirection, UpdateFrequency };

/**
 * Rommet Hypoteser, innsenderens side (H2.2). Nøklene eies her og bare her
 * (`react-ts-integration`, «Query-nøkler har én eier»); skjermene i `src/hypoteser/` får
 * data og kall gjennom hookene under, aldri gjennom den genererte klienten.
 */
export const hypoteseNoekkel = {
  alle: ['hypotheses'] as const,
  config: ['hypotheses', 'config'] as const,
  mine: ['hypotheses', 'liste', 'mine'] as const,
  nummer: (nr: number) => ['hypotheses', 'nummer', nr] as const,
  detalj: (id: string) => ['hypotheses', 'detalj', id] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/**
 * Rommets ordlyd, minimum og hjelpetekst (`GET /hypotheses/config`). Lesbar for bidragsyter;
 * `TenantSettings` er ikke det. Endres sjelden, men hentes på nytt når tjeneren sier at noe
 * mangler som klienten ikke så (minimumet kan være hevet siden siden ble åpnet).
 */
export function useHypoteseConfig(enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: hypoteseNoekkel.config,
    enabled,
    queryFn: async (): Promise<HypothesisConfig> => {
      const res = await hypothesesConfig({ client: klient });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as HypothesisConfig;
    },
  });
}

/** Detaljens nøkkel og henting — ÉN definisjon, brukt av `useHypotese` og `useHypoteser`. */
function detaljSporring(klient: ReturnType<typeof useApiKlient>, id: string) {
  return {
    queryKey: hypoteseNoekkel.detalj(id),
    queryFn: async (): Promise<Hypothesis> => {
      const res = await hypothesesGet({ client: klient, path: { id } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
  };
}

/** Én hypotese med historikken. Et utkast finnes bare for innsenderen; for alle andre 404. */
export function useHypotese(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    ...detaljSporring(klient, id ?? ''),
    enabled: id !== null,
    // Utkastet lever i skjemaets minne mens det skrives; en ny henting ville overskrevet det.
    staleTime: Infinity,
  });
}

/**
 * Flere hypoteser i detalj, med samme nøkkel og henting som `useHypotese`. «Til deg» trenger det
 * for eierforslaget: lista (`HypothesisSummary`) har ikke `ownerAcceptedAt`, så om eierskapet er
 * tatt, står bare i detaljen (hypothesis.tsp, `Hypothesis.ownerAcceptedAt`).
 */
export function useHypoteser(ider: readonly string[]) {
  const klient = useApiKlient();
  return useQueries({ queries: ider.map((id) => detaljSporring(klient, id)) });
}

/**
 * Henter utkastet slik tjeneren har det NÅ, forbi cachen — for 409 `draft_changed_elsewhere`,
 * der skjemaet må vise den andre versjonen før personen velger. Resultatet legges i cachen
 * under detaljens nøkkel, så resten av panelet ser det samme.
 */
export function useHentHypotesePaaNytt() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useCallback(
    async (id: string): Promise<Hypothesis> => {
      const res = await hypothesesGet({ client: klient, path: { id } });
      if (!res.response?.ok || !res.data) kast(res);
      const h = res.data as Hypothesis;
      queryClient.setQueryData(hypoteseNoekkel.detalj(id), h);
      return h;
    },
    [klient, queryClient],
  );
}

/** «Mine hypoteser» — det kalleren har sendt inn, utkast inkludert (`?mine=true`). */
export function useMineHypoteser() {
  const klient = useApiKlient();
  return useQuery({
    queryKey: hypoteseNoekkel.mine,
    queryFn: async (): Promise<HypothesisSummary[]> => {
      const res = await hypothesesList({ client: klient, query: { mine: true, limit: 100 } });
      if (!res.response?.ok || !res.data) kast(res);
      return (res.data as { items: HypothesisSummary[] }).items;
    },
  });
}

/** Oppslag på løpenummeret («H-142» i adressen), så detaljen kan hentes med id. */
export function useHypoteseMedNummer(nr: number | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: hypoteseNoekkel.nummer(nr ?? 0),
    enabled: nr !== null,
    queryFn: async (): Promise<Hypothesis | null> => {
      const liste = await hypothesesList({ client: klient, query: { number: String(nr) } });
      if (!liste.response?.ok || !liste.data) kast(liste);
      const treff = (liste.data as { items: HypothesisSummary[] }).items[0];
      if (!treff) return null;
      const res = await hypothesesGet({ client: klient, path: { id: treff.id } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
  });
}

/** Første lagring: `POST /hypotheses` oppretter utkastet (tomt eller halvskrevet). Ingen audit-rad. */
export function useOpprettUtkast() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: HypothesisCreate): Promise<Hypothesis> => {
      const res = await hypothesesCreate({ client: klient, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
    onSuccess: (h) => {
      queryClient.setQueryData(hypoteseNoekkel.detalj(h.id), h);
      void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.mine });
    },
  });
}

/**
 * Autolagring (`PATCH /{id}` med `baseVersion`). En eldre versjon gir 409
 * `draft_changed_elsewhere`, som skjemaet løser ved å la personen velge — aldri ved å sende
 * på nytt med tjenerens versjon uten å spørre.
 */
export function useLagreUtkast() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: HypothesisUpdate }): Promise<Hypothesis> => {
      const res = await hypothesesUpdate({ client: klient, path: { id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
    onSuccess: (h) => {
      queryClient.setQueryData(hypoteseNoekkel.detalj(h.id), h);
    },
  });
}

/** «Send inn» (`POST /{id}/submit`): nummeret deles ut i dette kallet. */
export function useSendInn() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Hypothesis> => {
      const res = await hypothesesSubmit({ client: klient, path: { id } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
    onSuccess: (h) => {
      queryClient.setQueryData(hypoteseNoekkel.detalj(h.id), h);
      if (h.number !== undefined) queryClient.setQueryData(hypoteseNoekkel.nummer(h.number), h);
      void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.mine });
    },
  });
}

/**
 * «Legg inn fra tidligere kanal» (H2.8, `POST /hypotheses/import`, bare eier): ett kall, ingen
 * utkast på tjeneren. Svaret er hypotesen i statusen historikken ender i, med nummer. Listene og
 * tellingene hentes på nytt — registeret har fått en rad.
 */
export function useLeggInnFraTidligereKanal() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: HypothesisImport): Promise<Hypothesis> => {
      const res = await hypothesesImportHypothesis({ client: klient, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Hypothesis;
    },
    onSuccess: (h) => {
      queryClient.setQueryData(hypoteseNoekkel.detalj(h.id), h);
      if (h.number !== undefined) queryClient.setQueryData(hypoteseNoekkel.nummer(h.number), h);
      // Listene (registeret, innboksen, «Mine») og tellingene — samme prefiks som useHypoteseBehandling.ts.
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'liste'] });
      void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'counts'] });
    },
  });
}

/** Leser rommets config på nytt — etter 409 `hypothesis_incomplete` som klienten ikke forutså. */
export function useOppfriskConfig() {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.config }), [queryClient]);
}
